import axios, { AxiosResponse } from 'axios';
import Cookies from 'js-cookie';
import accessToken from '|/helpers/access-token';
import { datadogRum } from '@datadog/browser-rum';

interface Props {
  experimentName: string;
  identifier: string;
  apiTimeout?: number;
  cacheMinutes?: number;
  scopeCacheToUser?: boolean;
}

type GetAssignedBucketProps = Omit<Props, 'identifier'> & Partial<Pick<Props, 'identifier'>>;

interface Response {
  content: {
    experimentName: string;
    bucketKey: string;
  };
}

interface Request {
  experimentName: string;
  identifier: string;
}

const bucketCookieName = (
  experimentName: string,
  identifier: string | undefined,
  scopeCacheToUser: boolean
) => (scopeCacheToUser ? identifier && `${experimentName}.${identifier}` : experimentName);

const GetAssignedBucket = async ({
  experimentName,
  identifier: identifierOverride,
  apiTimeout = 2000,
  cacheMinutes = 15,
  scopeCacheToUser = false
}: GetAssignedBucketProps) => {
  const { user } = accessToken();

  const identifier = identifierOverride ?? user.user_id;
  const cookieName = bucketCookieName(experimentName, identifier, scopeCacheToUser);
  const cachedCookie = cookieName ? Cookies.get(cookieName) : undefined;
  const hasCachedCookie = !!cachedCookie;

  if (hasCachedCookie) {
    return {
      assignedBucket: cachedCookie,
      experimentName
    };
  }

  if (!identifier) {
    return {
      assignedBucket: 'control',
      experimentName,
      hasErrors: false
    };
  }

  return axios
    .post<Request, AxiosResponse<Response>>(
      `${process.env.SPLITTEST_API_URL}/api/v2/buckets`,
      {
        experimentName,
        identifier
      },
      { timeout: apiTimeout }
    )
    .then(({ data: { content: { bucketKey = null } = {} } = {} }) => {
      if (cookieName && cacheMinutes !== 0 && bucketKey) {
        Cookies.set(cookieName, bucketKey, {
          expires: new Date(new Date().getTime() + cacheMinutes * 60 * 1000)
        });
      }

      const experimentObject = {
        assignedBucket: bucketKey,
        experimentName,
        hasErrors: false
      };
      datadogRum.addAction('user-bucketed', {
        experimentName,
        bucketKey
      });

      return experimentObject;
    })
    .catch(() => ({
      assignedBucket: null,
      experimentName,
      hasErrors: true
    }));
};

interface LookupAssignedBucketProps {
  experimentName: string;
  apiTimeout?: number;
  cacheMinutes?: number;
  signal?: AbortSignal;
  scopeCacheToUser?: boolean;
}

// `lookup=true` never assigns; it needs `allowLookupOnly` on the Divvy experiment and 400s
// without it. `nonSideEffecting` in the body is not a read — it persists an assignment.
export const lookupAssignedBucket = async ({
  experimentName,
  apiTimeout = 2000,
  cacheMinutes = 15,
  signal,
  scopeCacheToUser = false
}: LookupAssignedBucketProps): Promise<string | null> => {
  const { user } = accessToken();

  const cookieName = bucketCookieName(experimentName, user.user_id, scopeCacheToUser);
  const cachedCookie = cookieName ? Cookies.get(cookieName) : undefined;
  const hasCachedCookie = !!cachedCookie;

  if (hasCachedCookie) {
    return cachedCookie;
  }

  if (!user.user_id) return null;

  return axios
    .post<Request, AxiosResponse<Response>>(
      `${process.env.SPLITTEST_API_URL}/api/v2/buckets`,
      { experimentName, identifier: user.user_id },
      { params: { lookup: true }, timeout: apiTimeout, signal }
    )
    .then(({ data: { content: { bucketKey = null } = {} } = {} }) => {
      if (!bucketKey) return null;

      if (cookieName && cacheMinutes !== 0) {
        Cookies.set(cookieName, bucketKey, {
          expires: new Date(new Date().getTime() + cacheMinutes * 60 * 1000)
        });
      }

      return bucketKey;
    })
    .catch(() => null);
};

export default GetAssignedBucket;
